import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { DollarSign, Plus, Calculator, CheckCircle, BadgeCheck } from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPayoutCyclesPanel() {
  const [cycles, setCycles] = useState([]);
  const [selectedCycleId, setSelectedCycleId] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [start, setStart] = useState(new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10));
  const [end, setEnd] = useState(new Date().toISOString().slice(0, 10));
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const loadCycles = async () => {
    try {
      const r = await axios.get(`${API}/admin/payout-cycles`, { headers: auth() });
      setCycles(r.data?.cycles || []);
    } catch {
      setCycles([]);
    }
  };

  const loadPayouts = async (cycleId) => {
    try {
      const r = await axios.get(`${API}/admin/payouts?cycle_id=${cycleId}`, { headers: auth() });
      setPayouts(r.data?.payouts || []);
    } catch {
      setPayouts([]);
    }
  };

  useEffect(() => { loadCycles(); }, []);
  useEffect(() => { if (selectedCycleId) loadPayouts(selectedCycleId); }, [selectedCycleId]);

  const create = async () => {
    if (!start || !end) return toast.error('Pick a period');
    setBusy(true);
    try {
      const body = {
        period_start: new Date(start).toISOString(),
        period_end: new Date(end).toISOString(),
        cycle_name: name || `Cycle ${start} → ${end}`,
      };
      const r = await axios.post(`${API}/admin/payout-cycles`, body, { headers: { ...auth(), 'Content-Type': 'application/json' } });
      toast.success(`Cycle ${r.data.cycle.id.slice(0, 8)}… created (DRAFT)`);
      setSelectedCycleId(r.data.cycle.id);
      await loadCycles();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    } finally { setBusy(false); }
  };

  const calc = async (id) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/payout-cycles/${id}/calculate`, {}, { headers: auth() });
      toast.success(`Calculated. ${r.data.payouts_created} payouts.`);
      await loadCycles();
      await loadPayouts(id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Calculate failed');
    } finally { setBusy(false); }
  };

  const approve = async (id) => {
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/admin/payouts/bulk-approve`, { cycle_id: id }, { headers: { ...auth(), 'Content-Type': 'application/json' } });
      toast.success(`Approved ${r.data.approved_count} payouts. Cycle SCHEDULED.`);
      await loadCycles();
      await loadPayouts(id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approve failed');
    } finally { setBusy(false); }
  };

  const markPaid = async (id) => {
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/admin/payouts/mark-paid`, { cycle_id: id }, { headers: { ...auth(), 'Content-Type': 'application/json' } });
      toast.success(`${r.data.paid_count} payouts PAID. Cycle PAID.`);
      await loadCycles();
      await loadPayouts(id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Mark-paid failed');
    } finally { setBusy(false); }
  };

  const sc = (s) => ({
    DRAFT: '#6B7280', OPEN: '#6B7280', CALCULATING: '#0EA5E9',
    APPROVAL_REQUIRED: '#D97706', SCHEDULED: '#7C3AED', PAID: '#059669', CANCELED: '#9CA3AF',
  })[s] || '#6B7280';

  return (
    <div data-testid="admin-payout-cycles-panel" style={{ padding: 24 }}>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: theme.colors.text.primary, marginBottom: 16 }}>Payout Cycles</h2>

      <Card style={{ marginBottom: 24 }}>
        <CardContent className="pt-5 pb-5">
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <label style={{ fontSize: 12, color: theme.colors.text.muted, display: 'block', marginBottom: 4 }}>Period start</label>
              <input data-testid="cycle-start-input" type="date" value={start} onChange={(e) => setStart(e.target.value)}
                style={{ padding: '8px 10px', border: `1px solid ${theme.colors.borders}`, borderRadius: 6, fontSize: 13 }} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: theme.colors.text.muted, display: 'block', marginBottom: 4 }}>Period end</label>
              <input data-testid="cycle-end-input" type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                style={{ padding: '8px 10px', border: `1px solid ${theme.colors.borders}`, borderRadius: 6, fontSize: 13 }} />
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={{ fontSize: 12, color: theme.colors.text.muted, display: 'block', marginBottom: 4 }}>Cycle name (optional)</label>
              <input data-testid="cycle-name-input" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Optional name"
                style={{ width: '100%', padding: '8px 10px', border: `1px solid ${theme.colors.borders}`, borderRadius: 6, fontSize: 13 }} />
            </div>
            <Button data-testid="create-cycle-btn" onClick={create} disabled={busy}
              style={{ background: theme.colors.primary, color: '#FFF', gap: 6 }}>
              <Plus size={14} /> Create Cycle
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card style={{ marginBottom: 24 }}>
        <CardContent className="pt-5 pb-5">
          <h3 style={{ fontSize: 14, fontWeight: 700, color: theme.colors.text.primary, marginBottom: 12 }}>All cycles ({cycles.length})</h3>
          {cycles.length === 0 ? (
            <p style={{ fontSize: 13, color: theme.colors.text.muted }}>No cycles yet — create one above.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {cycles.map((c) => (
                <div key={c.id} data-testid={`cycle-row-${c.id}`}
                  onClick={() => setSelectedCycleId(c.id)}
                  style={{
                    padding: 12, borderRadius: 8, cursor: 'pointer',
                    border: `1px solid ${selectedCycleId === c.id ? theme.colors.primary : theme.colors.borders}`,
                    background: selectedCycleId === c.id ? `${theme.colors.primary}0A` : theme.colors.bg.secondary,
                    display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 12, alignItems: 'center',
                  }}>
                  <div>
                    <p style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text.primary }}>{c.cycle_name || c.id.slice(0, 8)}</p>
                    <p style={{ fontSize: 11, color: theme.colors.text.muted }}>
                      {(c.period_start || c.start_at || '').slice(0, 10)} → {(c.period_end || c.end_at || '').slice(0, 10)}
                    </p>
                  </div>
                  <span data-testid={`cycle-status-${c.id}`}
                    style={{ padding: '4px 10px', borderRadius: 14, fontSize: 11, fontWeight: 600, background: `${sc(c.status)}1A`, color: sc(c.status), justifySelf: 'start' }}>
                    {c.status}
                  </span>
                  <p style={{ fontSize: 12, color: theme.colors.text.secondary }}>
                    Net: ${Number((c.totals && c.totals.net_distributable) || 0).toFixed(2)} · {c.buyer_payouts_count ?? 0} payouts
                  </p>
                  <div style={{ display: 'flex', gap: 6 }} onClick={(e) => e.stopPropagation()}>
                    {(c.status === 'DRAFT' || c.status === 'OPEN' || c.status === 'APPROVAL_REQUIRED') && (
                      <Button data-testid={`btn-calc-${c.id}`} size="sm" variant="outline" onClick={() => calc(c.id)} disabled={busy} style={{ gap: 4 }}>
                        <Calculator size={12} /> Calc
                      </Button>
                    )}
                    {c.status === 'APPROVAL_REQUIRED' && (
                      <Button data-testid={`btn-approve-${c.id}`} size="sm" onClick={() => approve(c.id)} disabled={busy}
                        style={{ background: '#7C3AED', color: '#FFF', gap: 4 }}>
                        <CheckCircle size={12} /> Approve
                      </Button>
                    )}
                    {c.status === 'SCHEDULED' && (
                      <Button data-testid={`btn-paid-${c.id}`} size="sm" onClick={() => markPaid(c.id)} disabled={busy}
                        style={{ background: '#059669', color: '#FFF', gap: 4 }}>
                        <BadgeCheck size={12} /> Mark Paid
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {selectedCycleId && (
        <Card>
          <CardContent className="pt-5 pb-5">
            <h3 style={{ fontSize: 14, fontWeight: 700, color: theme.colors.text.primary, marginBottom: 12 }}>
              <DollarSign size={14} style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }} />
              Payouts in cycle ({payouts.length})
            </h3>
            {payouts.length === 0 ? (
              <p style={{ fontSize: 13, color: theme.colors.text.muted }}>No payouts yet — calculate the cycle first.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {payouts.map((p) => (
                  <div key={p.id} data-testid={`payout-row-${p.id}`}
                    style={{
                      padding: '10px 14px', borderRadius: 6, background: theme.colors.bg.secondary,
                      border: `1px solid ${theme.colors.borders}`, display: 'grid',
                      gridTemplateColumns: '2fr 2fr 1fr 100px', gap: 8, alignItems: 'center',
                    }}>
                    <span style={{ fontSize: 12, color: theme.colors.text.primary }}>{p.portfolio_name}</span>
                    <span style={{ fontSize: 12, color: theme.colors.text.secondary }}>{p.buyer_email}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#059669' }}>${Number(p.amount || p.amount_usd || 0).toFixed(2)}</span>
                    <span style={{ padding: '3px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: `${sc(p.status)}1A`, color: sc(p.status), justifySelf: 'start' }}>
                      {p.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
